import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import OrderForm from './OrderForm';
import ShoppingCartDisplay from './ShoppingCart';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { easterProducts } from '../data/easterProducts';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const [currentImage, setCurrentImage] = useState(0);

  return (
    <Card
      className="overflow-hidden border-2 border-amber-200 shadow-lg bg-white rounded-2xl flex flex-col cursor-pointer hover:shadow-xl hover:border-orange-300 transition-all"
      onClick={() => navigate(`/pascoa/${product.id}`)}
    >
      {/* Image Gallery */}
      <div className="relative h-56 bg-amber-50 flex-shrink-0">
        <img
          src={product.images[currentImage]}
          alt={product.name}
          className="w-full h-full object-cover"
        />
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setCurrentImage(p => (p === 0 ? product.images.length - 1 : p - 1)); }}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/85 hover:bg-white text-amber-900 shadow flex items-center justify-center transition-colors"
          aria-label="Imagem anterior"
        >
          <ArrowLeft className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setCurrentImage(p => (p + 1) % product.images.length); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/85 hover:bg-white text-amber-900 shadow flex items-center justify-center transition-colors"
          aria-label="Próxima imagem"
        >
          <ArrowRight className="w-3 h-3" />
        </button>
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {product.images.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={e => { e.stopPropagation(); setCurrentImage(idx); }}
              className={`h-2 rounded-full transition-all ${
                idx === currentImage ? 'w-6 bg-white' : 'w-2 bg-white/60 hover:bg-white/90'
              }`}
              aria-label={`Foto ${idx + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Product Info */}
      <CardContent className="p-5 flex flex-col gap-2">
        <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-200 w-fit text-xs">
          Edição de Páscoa 2026
        </Badge>
        <h3 className="text-lg font-bold text-amber-950">{product.name}</h3>
        <div className="text-xl font-bold text-orange-800">
          {product.priceFormatted} <span className="text-sm font-normal text-amber-600">{product.priceSuffix || '/ pack'}</span>
        </div>
        <span className="text-xs text-amber-500 mt-1">Clique para ver detalhes →</span>
      </CardContent>
    </Card>
  );
};

const PascoaPage = () => {
  return (
    <div className="min-h-screen bg-amber-50">
      <Header />

      <main className="pt-24">
        {/* Hero */}
        <section className="pt-6 pb-2 bg-amber-50 text-center">
          <div className="container mx-auto px-4 max-w-2xl relative z-10">
            <Badge className="mb-3 bg-orange-100 text-orange-800 hover:bg-orange-200">
              Coleção Especial 2026
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-amber-950 mb-2">
              Páscoa <span className="text-orange-700">Ser Tão Doce</span>
            </h1>
          </div>
        </section>

        {/* Products */}
        <section className="pt-2 pb-14 -mt-2">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-2xl font-bold text-amber-950 mb-8">
              Produtos de Páscoa
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
              {easterProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>

        {/* Cart */}
        <section className="py-12 bg-amber-50/60">
          <div className="container mx-auto px-4 max-w-lg" id="pascoa-cart">
            <ShoppingCartDisplay />
          </div>
        </section>

        {/* Order Form */}
        <OrderForm />
      </main>

      <Footer />
    </div>
  );
};

export default PascoaPage;
